import { useState, useEffect, useCallback } from 'react';
import { Settings, X, Check, Info, RefreshCw, Plus, Wifi, WifiOff, Copy } from 'lucide-react';
import { listPlatforms, updatePlatform, updateCustomerPlatform, listVoiceUris,
         enableCustomerPlatform, disableCustomerPlatform, createCustomPlatform, deleteCustomPlatform,
         getTeamsConnection, saveTeamsConnection, deleteTeamsConnection, testTeamsConnection,
         getTeamsUsers, syncTeamsUsers, assignTeamsNumber, getTeamsOAuthUrl,
         listVoiceRoutingPolicies, addVoiceRoutingPolicy, syncVoiceRoutingPolicies, deleteVoiceRoutingPolicy,
         getZoomConnection, saveZoomConnection, deleteZoomConnection, testZoomConnection, getZoomUsers, syncZoomUsers } from '../services/portalApi';

// ── Microsoft Teams Tenant Connector ─────────────────────────────────────────

function TeamsConnector({ customerId, adminMode, onStatusChange }) {
  const [conn,        setConn]        = useState(null);
  const [users,       setUsers]       = useState([]);
  const [loadingConn, setLoadingConn] = useState(true);
  const [loadingUsers,setLoadingUsers]= useState(false);
  const [syncing,     setSyncing]     = useState(false);
  const [testing,     setTesting]     = useState(false);
  const [showSetup,   setShowSetup]   = useState(false);
  const [form,        setForm]        = useState({ tenantId: '', clientId: '', clientSecret: '', displayName: '' });
  const [formErr,     setFormErr]     = useState('');
  const [flash,       setFlash]       = useState(null);
  const [assignTarget, setAssignTarget] = useState(null); // { msUserId, upn, displayName, phone }
  const [assignNumber, setAssignNumber] = useState('');
  const [assigning,    setAssigning]   = useState(false);
  const [psCmd,        setPsCmd]       = useState('');
  const [connecting,  setConnecting]  = useState(false);
  const [oauthAvail,  setOauthAvail]  = useState(true); // assume available until 503

  // VRP management state
  const [vrps,          setVrps]         = useState([]);
  const [vrpAddName,    setVrpAddName]   = useState('');
  const [vrpAddDesc,    setVrpAddDesc]   = useState('');
  const [syncingVrp,    setSyncingVrp]   = useState(false);
  const [addingVrp,     setAddingVrp]    = useState(false);
  const [deletingVrpId, setDeletingVrpId]= useState(null);
  const [vrpErr,        setVrpErr]       = useState('');

  const cid = customerId || undefined;

  const flashMsg = (type, text) => { setFlash({ type, text }); setTimeout(() => setFlash(null), 5000); };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await getTeamsUsers(cid);
      setUsers(data.users || []);
    } catch {}
    finally { setLoadingUsers(false); }
  };

  const loadVrps = async () => {
    try {
      const data = await listVoiceRoutingPolicies(cid);
      setVrps(data.policies || []);
    } catch {}
  };

  const loadConn = useCallback(async () => {
    setLoadingConn(true);
    try {
      const data = await getTeamsConnection(cid);
      setConn(data);
      if (data.connected) { loadUsers(); loadVrps(); }
    } catch {}
    finally { setLoadingConn(false); }
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConn(); }, [loadConn]);

  const handleOAuthConnect = async () => {
    setConnecting(true);
    try {
      const data = await getTeamsOAuthUrl(cid);
      if (!data?.url) throw new Error('No authorization URL returned');

      const popup = window.open(data.url, 'ms365-auth',
        'width=520,height=660,scrollbars=yes,resizable=yes,toolbar=no,menubar=no');
      if (!popup) {
        flashMsg('error', 'Popup blocked — please allow popups for this site and try again');
        setConnecting(false);
        return;
      }

      const handler = (event) => {
        if (event.data && typeof event.data.ok === 'boolean') {
          window.removeEventListener('message', handler);
          clearInterval(pollTimer);
          if (event.data.ok) {
            flashMsg('success', 'Microsoft 365 connected successfully!');
            loadConn();
            onStatusChange?.();
          } else {
            flashMsg('error', `Connection failed: ${event.data.error || 'Unknown error'}`);
          }
          setConnecting(false);
        }
      };
      window.addEventListener('message', handler);

      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handler);
          setConnecting(false);
        }
      }, 1000);

    } catch (e) {
      if (e.message.includes('not configured') || e.message.includes('503')) {
        setOauthAvail(false);
        setShowSetup(true); // fall back to manual entry
      } else {
        flashMsg('error', e.message);
      }
      setConnecting(false);
    }
  };

  const handleSaveSetup = async () => {
    if (!form.tenantId.trim() || !form.clientId.trim() || !form.clientSecret.trim()) {
      setFormErr('Tenant ID, Client ID, and Client Secret are required'); return;
    }
    setFormErr('');
    try {
      await saveTeamsConnection({ ...form, customerId: cid });
      setShowSetup(false);
      await loadConn();
      flashMsg('success', 'Connection saved — click "Test Connection" to verify');
      onStatusChange?.();
    } catch (e) { setFormErr(e.message); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testTeamsConnection(cid);
      setConn(prev => ({ ...prev, connected: true, status: 'connected' }));
      flashMsg('success', `Connected to "${res.orgName}" successfully`);
      onStatusChange?.();
    } catch (e) { flashMsg('error', `Connection failed: ${e.message}`); }
    finally { setTesting(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncTeamsUsers(cid);
      await loadUsers();
      flashMsg('success', `Synced ${res.synced} users from Microsoft 365`);
    } catch (e) { flashMsg('error', `Sync failed: ${e.message}`); }
    finally { setSyncing(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Remove Teams tenant connection? Synced user data will be deleted.')) return;
    await deleteTeamsConnection(cid);
    setConn({ connected: false });
    setUsers([]);
    onStatusChange?.();
  };

  const handleAssign = async () => {
    if (!assignTarget) return;
    setAssigning(true);
    try {
      const res = await assignTeamsNumber(assignTarget.msUserId, assignNumber, assignTarget.upn, assignTarget.phone, cid);
      if (res.queued) {
        setUsers(prev => prev.map(u => u.msUserId === assignTarget.msUserId ? { ...u, phoneNumber: assignNumber } : u));
        flashMsg('success', assignNumber ? `${assignNumber} assigned to ${assignTarget.displayName}` : `Phone number removed from ${assignTarget.displayName}`);
        setAssignTarget(null);
        setPsCmd('');
        setAssignNumber('');
      } else if (res.permissionMissing) {
        setPsCmd(res.psCommand || '');
        flashMsg('error', `Permission required: ${res.permissionName} — re-run Connect with Microsoft 365, or use the PowerShell command below`);
      }
    } catch (e) {
      flashMsg('error', `Assignment failed: ${e.message}`);
    } finally { setAssigning(false); }
  };

  if (loadingConn) return <div style={{ padding: '12px 0', color: 'var(--muted-3)', fontSize: 13 }}>Loading connection…</div>;

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {conn?.connected
            ? <Wifi size={14} color="var(--ok)"/>
            : <WifiOff size={14} color="var(--muted-3)"/>
          }
          <span style={{ fontSize: 13, fontWeight: 600, color: conn?.connected ? 'var(--ok)' : 'var(--muted-2)' }}>
            {conn?.connected ? `Teams Connected${conn.displayName ? ` · ${conn.displayName}` : ''}` : 'Teams Tenant Not Connected'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {conn?.connected && (
            <>
              <button className="btn btn-secondary btn-xs" onClick={handleSync} disabled={syncing}>
                <RefreshCw size={11} style={syncing ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                {syncing ? 'Syncing…' : 'Sync Users'}
              </button>
              <button className="btn btn-secondary btn-xs" onClick={handleDisconnect} style={{ color: 'var(--err)' }}>
                Disconnect
              </button>
            </>
          )}
          {!conn?.connected && (
            <div style={{ display: 'flex', gap: 6 }}>
              {oauthAvail && (
                <button
                  className="btn btn-primary btn-xs"
                  onClick={handleOAuthConnect}
                  disabled={connecting}
                  style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  {connecting
                    ? <RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }}/>
                    : <svg width="11" height="11" viewBox="0 0 21 21" fill="none" style={{ flexShrink: 0 }}>
                        <rect width="10" height="10" fill="#f25022"/><rect x="11" width="10" height="10" fill="#7fba00"/>
                        <rect y="11" width="10" height="10" fill="#00a4ef"/><rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
                      </svg>
                  }
                  {connecting ? 'Connecting…' : 'Connect with Microsoft 365'}
                </button>
              )}
              <button
                className="btn btn-secondary btn-xs"
                onClick={() => setShowSetup(s => !s)}
                title="Enter credentials manually"
              >
                Manual setup
              </button>
            </div>
          )}
          {(conn?.status === 'pending' || conn?.status === 'error') && !conn?.connected && (
            <button className="btn btn-secondary btn-xs" onClick={handleTest} disabled={testing}>
              {testing ? <RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }}/> : <Check size={11}/>}
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
          )}
        </div>
      </div>

      {flash && (
        <div style={{
          padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10,
          background: flash.type === 'success' ? 'var(--ok-bg)' : 'var(--err-bg)',
          color:      flash.type === 'success' ? 'var(--ok)' : 'var(--err)',
          border:     `1px solid ${flash.type === 'success' ? 'var(--ok-bg)' : 'var(--err-btn-bg)'}`,
        }}>{flash.text}</div>
      )}

      {/* Setup form */}
      {showSetup && (
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            <strong style={{ color: 'var(--warn)' }}>Manual setup</strong> — use this if the automated flow isn't available.
            Create an <strong style={{ color: 'var(--text-2)' }}>Azure AD App Registration</strong> in the customer's tenant with Graph API Application permissions:
            {' '}<code style={{ color: 'var(--cyan)' }}>User.Read.All</code>, <code style={{ color: 'var(--cyan)' }}>User.ReadWrite.All</code>, <code style={{ color: 'var(--cyan)' }}>Directory.Read.All</code>, <code style={{ color: 'var(--cyan)' }}>TeamsTelephoneNumber.ReadWrite.All</code>.
            Grant admin consent, then paste credentials below.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Tenant ID (Directory ID)</label>
              <input className="form-input font-mono" style={{ fontSize: 11 }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={form.tenantId} onChange={e => setForm(f => ({ ...f, tenantId: e.target.value }))}/>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Application (Client) ID</label>
              <input className="form-input font-mono" style={{ fontSize: 11 }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}/>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Client Secret</label>
              <input className="form-input font-mono" style={{ fontSize: 11 }} type="password" placeholder="App secret value"
                value={form.clientSecret} onChange={e => setForm(f => ({ ...f, clientSecret: e.target.value }))}/>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Connection Name (optional)</label>
              <input className="form-input" style={{ fontSize: 11 }} placeholder="e.g. Contoso Microsoft 365"
                value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}/>
            </div>
          </div>
          {formErr && <div style={{ fontSize: 11, color: 'var(--err)', marginBottom: 8 }}>{formErr}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-xs" onClick={() => setShowSetup(false)}>Cancel</button>
            <button className="btn btn-primary btn-xs" onClick={handleSaveSetup}>
              <Check size={11}/> Save & Connect
            </button>
          </div>
        </div>
      )}

      {/* Users list */}
      {conn?.connected && (
        <div>
          {loadingUsers ? (
            <div style={{ color: 'var(--muted-3)', fontSize: 12, padding: '8px 0' }}>Loading users…</div>
          ) : users.length === 0 ? (
            <div style={{ color: 'var(--muted-3)', fontSize: 12, padding: '8px 0' }}>
              No users synced yet. Click "Sync Users" to load from Microsoft 365.
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto', borderRadius: 6, border: '1px solid var(--border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>User</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>Phone Number</th>
                    <th style={{ padding: '8px 8px', textAlign: 'center', color: 'var(--muted-3)', fontWeight: 500, width: 80 }}>Assign</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.msUserId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ fontWeight: 500, color: 'var(--text-2)' }}>{u.displayName}</div>
                        <div style={{ color: 'var(--muted-3)', fontSize: 11 }}>{u.upn}</div>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {u.phoneNumber
                          ? <span style={{ fontFamily: 'monospace', color: 'var(--ok)', fontSize: 11 }}>{u.phoneNumber}</span>
                          : <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>—</span>
                        }
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <button
                          className="btn btn-secondary btn-xs"
                          style={{ fontSize: 10 }}
                          onClick={() => { setAssignTarget({ msUserId: u.msUserId, upn: u.upn, displayName: u.displayName, phone: u.phoneNumber }); setAssignNumber(u.phoneNumber || ''); }}
                        >
                          Assign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 8 }}>
            {users.length} user{users.length !== 1 ? 's' : ''}{conn.lastSynced ? ` · last synced ${conn.lastSynced?.slice(0, 10)}` : ''}
          </div>

          {/* Voice Routing Policies */}
          <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>
                Voice Routing Policies
                {vrps.length > 0 && (
                  <span style={{ marginLeft: 8, background: 'var(--border)', color: 'var(--info)', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>
                    {vrps.length}
                  </span>
                )}
              </div>
              <button
                className="btn btn-secondary btn-xs"
                disabled={syncingVrp}
                onClick={async () => {
                  setSyncingVrp(true); setVrpErr('');
                  try {
                    const r = await syncVoiceRoutingPolicies(cid);
                    flashMsg('success', `Synced ${r.synced} voice routing polic${r.synced === 1 ? 'y' : 'ies'} from Microsoft 365`);
                    loadVrps();
                  } catch (e) {
                    if (e.message.includes('Missing permission') || e.message.includes('TeamsUserConfiguration')) {
                      setVrpErr("Add 'TeamsUserConfiguration.Read.All' Application permission to your Azure AD app registration, grant admin consent, then retry.");
                    } else {
                      setVrpErr(e.message);
                    }
                  }
                  finally { setSyncingVrp(false); }
                }}
              >
                <RefreshCw size={11} style={syncingVrp ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                {syncingVrp ? 'Syncing…' : 'Sync from M365'}
              </button>
            </div>

            {/* Description */}
            <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 10, lineHeight: 1.5 }}>
              Discovers voice routing policies assigned to users in this tenant via Microsoft Graph. Requires the{' '}
              <code style={{ color: 'var(--cyan)', fontSize: 10 }}>TeamsUserConfiguration.Read.All</code>{' '}
              application permission on your Azure AD app.
            </div>

            {vrpErr && (
              <div style={{ fontSize: 11, color: 'var(--err)', marginBottom: 10, background: 'var(--err-bg)', border: '1px solid var(--err-btn-bg)', borderRadius: 6, padding: '8px 10px' }}>
                {vrpErr}
              </div>
            )}

            {/* Manual add */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: 'var(--muted-3)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.4px' }}>Add policy manually</div>
                <input
                  className="form-input"
                  style={{ fontSize: 11, padding: '4px 8px' }}
                  placeholder="Policy name"
                  value={vrpAddName}
                  onChange={e => { setVrpAddName(e.target.value); setVrpErr(''); }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: 'var(--muted-3)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.4px' }}>Description (optional)</div>
                <input
                  className="form-input"
                  style={{ fontSize: 11, padding: '4px 8px' }}
                  placeholder="Description"
                  value={vrpAddDesc}
                  onChange={e => setVrpAddDesc(e.target.value)}
                />
              </div>
              <button
                className="btn btn-secondary btn-xs"
                disabled={addingVrp || !vrpAddName.trim()}
                onClick={async () => {
                  setAddingVrp(true); setVrpErr('');
                  try {
                    await addVoiceRoutingPolicy(cid, vrpAddName.trim(), vrpAddDesc.trim());
                    setVrpAddName(''); setVrpAddDesc('');
                    loadVrps();
                  } catch (e) { setVrpErr(e.message); }
                  finally { setAddingVrp(false); }
                }}
              >
                {addingVrp ? <RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }}/> : <Plus size={11}/>}
                Add
              </button>
            </div>

            {/* Policy list */}
            {vrps.length > 0 && (
              <div style={{ borderRadius: 6, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>Name</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>Last Synced</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {vrps.map(v => (
                      <tr key={v.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 10px', color: 'var(--text-2)', fontWeight: 500 }}>{v.name}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--muted-3)', fontSize: 10 }}>
                          {v.synced_at ? v.synced_at.slice(0, 16).replace('T', ' ') : '—'}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          <button
                            className="btn btn-secondary btn-xs"
                            style={{ color: 'var(--err)', padding: '2px 6px' }}
                            disabled={deletingVrpId === v.id}
                            onClick={async () => {
                              setDeletingVrpId(v.id);
                              try { await deleteVoiceRoutingPolicy(v.id); loadVrps(); }
                              catch (e) { setVrpErr(e.message); }
                              finally { setDeletingVrpId(null); }
                            }}
                          >
                            <X size={10}/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {vrps.length === 0 && (
              <div style={{ fontSize: 11, color: 'var(--muted-3)', textAlign: 'center', padding: '12px 0' }}>
                No policies stored. Click "Sync from M365" to discover them automatically, or add manually above.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign number modal */}
      {assignTarget && (
        <div className="modal-overlay" onClick={() => setAssignTarget(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Assign Phone Number</div>
                <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2 }}>{assignTarget.displayName} · {assignTarget.upn}</div>
              </div>
              <button className="btn btn-secondary btn-xs" onClick={() => setAssignTarget(null)}><X size={12}/></button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: psCmd ? 12 : 0 }}>
                <label className="form-label">E.164 Phone Number</label>
                <input
                  className="form-input font-mono"
                  placeholder="+441234567890"
                  value={assignNumber}
                  onChange={e => { setAssignNumber(e.target.value); setPsCmd(''); }}
                />
                <div className="form-hint">Leave blank to remove phone number. Must be in E.164 format.</div>
              </div>
              {psCmd && (
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>PowerShell fallback (run in Teams PowerShell module):</span>
                    <button
                      onClick={() => navigator.clipboard.writeText(psCmd)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-3)', padding: 2, display: 'flex', alignItems: 'center', gap: 3 }}
                    >
                      <Copy size={11}/> <span style={{ fontSize: 10 }}>Copy</span>
                    </button>
                  </div>
                  <code style={{ fontSize: 10, color: 'var(--cyan)', wordBreak: 'break-all' }}>{psCmd}</code>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setAssignTarget(null); setPsCmd(''); }}>Close</button>
              <button className="btn btn-primary" onClick={handleAssign} disabled={assigning}>
                {assigning ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }}/> : <Check size={12}/>}
                {assignNumber ? 'Assign Number' : 'Remove Number'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Zoom Phone Connector ──────────────────────────────────────────────────────

function ZoomConnector({ customerId, onStatusChange }) {
  const [conn,         setConn]         = useState(null);
  const [users,        setUsers]        = useState([]);
  const [loadingConn,  setLoadingConn]  = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [syncing,      setSyncing]      = useState(false);
  const [testing,      setTesting]      = useState(false);
  const [showSetup,    setShowSetup]    = useState(false);
  const [form,         setForm]         = useState({ accountId: '', clientId: '', clientSecret: '', displayName: '' });
  const [formErr,      setFormErr]      = useState('');
  const [flash,        setFlash]        = useState(null);

  const cid = customerId || undefined;
  const flashMsg = (type, text) => { setFlash({ type, text }); setTimeout(() => setFlash(null), 5000); };

  const loadUsers = async () => {
    if (!cid) return;
    setLoadingUsers(true);
    try { const d = await getZoomUsers(cid); setUsers(d.users || []); } catch {}
    finally { setLoadingUsers(false); }
  };

  const loadConn = useCallback(async () => {
    if (!cid) { setLoadingConn(false); return; }
    setLoadingConn(true);
    try {
      const data = await getZoomConnection(cid);
      setConn(data);
      if (data.connected) loadUsers();
    } catch {}
    finally { setLoadingConn(false); }
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConn(); }, [loadConn]);

  const handleSave = async () => {
    if (!form.accountId.trim() || !form.clientId.trim() || !form.clientSecret.trim()) {
      setFormErr('Account ID, Client ID, and Client Secret are required'); return;
    }
    setFormErr('');
    try {
      await saveZoomConnection({ ...form, customerId: cid });
      setShowSetup(false);
      await loadConn();
      flashMsg('success', 'Zoom credentials saved — click "Test Connection" to verify');
      onStatusChange?.();
    } catch (e) { setFormErr(e.message); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testZoomConnection(cid);
      setConn(prev => ({ ...prev, connected: true, status: 'connected' }));
      flashMsg('success', `Connected — ${res.userCount} Zoom Phone user${res.userCount !== 1 ? 's' : ''} found`);
      loadUsers();
      onStatusChange?.();
    } catch (e) { flashMsg('error', `Connection failed: ${e.message}`); }
    finally { setTesting(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncZoomUsers(cid);
      await loadUsers();
      flashMsg('success', `Synced ${res.synced} users from Zoom`);
    } catch (e) { flashMsg('error', `Sync failed: ${e.message}`); }
    finally { setSyncing(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Remove Zoom connection? Synced user data will be deleted.')) return;
    await deleteZoomConnection(cid);
    setConn({ connected: false });
    setUsers([]);
    onStatusChange?.();
  };

  // Unique calling plans from synced users
  const callingPlans = [...new Set(users.map(u => u.callingPlan).filter(Boolean))];

  if (loadingConn) return <div style={{ padding: '12px 0', color: 'var(--muted-3)', fontSize: 13 }}>Loading Zoom connection…</div>;

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {conn?.connected
            ? <Wifi size={14} color="#2d8cff"/>
            : <WifiOff size={14} color="var(--muted-3)"/>
          }
          <span style={{ fontSize: 13, fontWeight: 600, color: conn?.connected ? '#2d8cff' : 'var(--muted-2)' }}>
            {conn?.connected
              ? `Zoom Connected${conn.displayName ? ` · ${conn.displayName}` : ''}`
              : 'Zoom Account Not Connected'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {conn?.connected && (
            <>
              <button className="btn btn-secondary btn-xs" onClick={handleSync} disabled={syncing}>
                <RefreshCw size={11} style={syncing ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                {syncing ? 'Syncing…' : 'Sync Users'}
              </button>
              <button className="btn btn-secondary btn-xs" onClick={handleDisconnect} style={{ color: 'var(--err)' }}>
                Disconnect
              </button>
            </>
          )}
          {!conn?.connected && (
            <button className="btn btn-secondary btn-xs" onClick={() => setShowSetup(s => !s)}>
              <Settings size={11}/> Configure
            </button>
          )}
          {(conn?.status === 'pending' || conn?.status === 'error') && !conn?.connected && (
            <button className="btn btn-secondary btn-xs" onClick={handleTest} disabled={testing}>
              {testing ? <RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }}/> : <Check size={11}/>}
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
          )}
        </div>
      </div>

      {flash && (
        <div style={{
          padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10,
          background: flash.type === 'success' ? 'var(--ok-bg)' : 'var(--err-bg)',
          color:      flash.type === 'success' ? 'var(--ok)' : 'var(--err)',
          border:     `1px solid ${flash.type === 'success' ? 'var(--ok-bg)' : 'var(--err-btn-bg)'}`,
        }}>{flash.text}</div>
      )}

      {showSetup && (
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            <strong style={{ color: 'var(--warn)' }}>Server-to-Server OAuth</strong> — create a Server-to-Server OAuth app in the{' '}
            <strong style={{ color: 'var(--text-2)' }}>Zoom App Marketplace</strong> with scopes:{' '}
            <code style={{ color: 'var(--cyan)' }}>phone:read:admin</code>,{' '}
            <code style={{ color: 'var(--cyan)' }}>phone:write:admin</code>.
            Then paste the credentials below.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Account ID</label>
              <input className="form-input font-mono" style={{ fontSize: 11 }} placeholder="your_account_id"
                value={form.accountId} onChange={e => setForm(f => ({ ...f, accountId: e.target.value }))}/>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Client ID</label>
              <input className="form-input font-mono" style={{ fontSize: 11 }} placeholder="your_client_id"
                value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}/>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Client Secret</label>
              <input className="form-input font-mono" style={{ fontSize: 11 }} type="password" placeholder="your_client_secret"
                value={form.clientSecret} onChange={e => setForm(f => ({ ...f, clientSecret: e.target.value }))}/>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Connection Name (optional)</label>
              <input className="form-input" style={{ fontSize: 11 }} placeholder="e.g. Acme Zoom Phone"
                value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}/>
            </div>
          </div>
          {formErr && <div style={{ fontSize: 11, color: 'var(--err)', marginBottom: 8 }}>{formErr}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-xs" onClick={() => setShowSetup(false)}>Cancel</button>
            <button className="btn btn-primary btn-xs" onClick={handleSave}>
              <Check size={11}/> Save Credentials
            </button>
          </div>
        </div>
      )}

      {conn?.connected && (
        <div>
          {loadingUsers ? (
            <div style={{ color: 'var(--muted-3)', fontSize: 12, padding: '8px 0' }}>Loading users…</div>
          ) : users.length === 0 ? (
            <div style={{ color: 'var(--muted-3)', fontSize: 12, padding: '8px 0' }}>
              No users synced yet. Click "Sync Users" to load from Zoom.
            </div>
          ) : (
            <>
              <div style={{ maxHeight: 280, overflowY: 'auto', borderRadius: 6, border: '1px solid var(--border)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>User</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>Phone Number</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>Calling Plan</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500 }}>Site</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.zoomUserId} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ fontWeight: 500, color: 'var(--text-2)' }}>{u.displayName}</div>
                          <div style={{ color: 'var(--muted-3)', fontSize: 11 }}>{u.email}</div>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {u.phoneNumber
                            ? <span style={{ fontFamily: 'monospace', color: 'var(--ok)', fontSize: 11 }}>{u.phoneNumber}</span>
                            : <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {u.callingPlan
                            ? <span style={{ fontSize: 11, color: '#2d8cff' }}>{u.callingPlan}</span>
                            : <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {u.siteName
                            ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>{u.siteName}</span>
                            : <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 8, display: 'flex', gap: 12 }}>
                <span>{users.length} user{users.length !== 1 ? 's' : ''}{conn.lastSynced ? ` · last synced ${conn.lastSynced.slice(0, 10)}` : ''}</span>
                {callingPlans.length > 0 && (
                  <span style={{ color: 'var(--muted-3)' }}>Plans: {callingPlans.join(', ')}</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Platform configure modal ──────────────────────────────────────────────────

function ConfigureModal({ platform, voiceUris, onClose, onSave, user, viewAsCustomer }) {
  const [url,     setUrl]     = useState(platform.url || '');
  const [primary, setPrimary] = useState(String(platform.primaryVoiceUriId || ''));
  const [alt,     setAlt]     = useState(String(platform.altVoiceUriId     || ''));
  const [dialPlanName, setDialPlanName] = useState(platform.dialPlanName || '');
  const [sbcTag,       setSbcTag]       = useState(platform.sbcTag       || '');
  const [saving,  setSaving]  = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(platform.id, {
        url,
        primaryVoiceUriId: primary ? Number(primary) : null,
        altVoiceUriId:     alt     ? Number(alt)     : null,
        dialPlanName: dialPlanName.trim(),
        sbcTag:       sbcTag.trim(),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const uriLabel = (u) => u.description ? `${u.description} — ${u.uri}` : u.uri;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: platform.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: platform.color, fontSize: 11, fontWeight: 800 }}>{platform.abbr}</span>
              </div>
              {platform.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2 }}>
              {viewAsCustomer
                ? `Customer-specific configuration for ${viewAsCustomer.name}`
                : !user?.isAdmin
                  ? 'Your platform configuration'
                  : 'Global default configuration'
              }
            </div>
          </div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>

        <div className="modal-body">
          <div className="alert alert-info">
            <Info size={14} style={{ flexShrink: 0 }}/>
            <span>
              {viewAsCustomer
                ? <>Configure <strong>{platform.name}</strong> for <strong>{viewAsCustomer.name}</strong>. These settings override global defaults for this customer.</>
                : !user?.isAdmin
                  ? <>Configure your connection to <strong>{platform.name}</strong>. Enter your SBC's SIP Trunk URL.</>
                  : <>Set global defaults for <strong>{platform.name}</strong>. Customers without their own config will use these settings.</>
              }
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">SIP Trunk URL</label>
            <input
              className="form-input font-mono"
              placeholder="sip:sbc.example.com"
              value={url}
              onChange={e => setUrl(e.target.value)}
            />
            <div className="form-hint">Used to auto-create a Voice URI in Voxbone on first activation if none is linked below</div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)', display: 'inline-block' }}/>
              Primary Voice URI <span style={{ fontWeight: 400, color: 'var(--muted-3)' }}>(optional)</span>
            </label>
            <select className="form-input select-input" value={primary} onChange={e => setPrimary(e.target.value)}>
              <option value="">— Not linked —</option>
              {voiceUris.map(u => (
                <option key={u.id} value={u.id}>{uriLabel(u)}</option>
              ))}
            </select>
            <div className="form-hint">Used when customers activate normal routing to this platform</div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warn)', display: 'inline-block' }}/>
              Fallback Voice URI <span style={{ fontWeight: 400, color: 'var(--muted-3)' }}>(optional)</span>
            </label>
            <select className="form-input select-input" value={alt} onChange={e => setAlt(e.target.value)}>
              <option value="">— Not linked —</option>
              {voiceUris.map(u => (
                <option key={u.id} value={u.id}>{uriLabel(u)}</option>
              ))}
            </select>
            <div className="form-hint">Used when customers activate DR failover routing to this platform</div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0 16px' }}/>
          <div style={{ fontSize: 12, color: 'var(--muted-3)', marginBottom: 12, lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--text-2)' }}>AudioCodes SBC dial plan.</strong> When a number is routed to this
            platform, a rule is pushed to every SBC linked to the Voice URI above — into the named dial plan, with this tag.
          </div>

          <div className="form-group">
            <label className="form-label">Dial Plan name</label>
            <input
              className="form-input"
              placeholder="e.g. Teams-Inbound"
              value={dialPlanName}
              onChange={e => setDialPlanName(e.target.value)}
            />
            <div className="form-hint">The AudioCodes dial plan to add the routing rule to. Leave blank to disable the SBC push for this platform.</div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">SBC tag</label>
            <input
              className="form-input font-mono"
              placeholder="e.g. TEAMS"
              value={sbcTag}
              onChange={e => setSbcTag(e.target.value)}
            />
            <div className="form-hint">
              Tag applied to the dial-plan rule — supports placeholders, resolved per SBC:
              <br/><code>{'{fqdn}'}</code> = that SBC's FQDN · <code>{'{account_code}'}</code> = the customer's account code (derived trunking).
              <br/><strong>Blank</strong> → each SBC's own FQDN (e.g. ireland… vs netherlands…).
              {' '}<code>{'{account_code}.{fqdn}'}</code> → e.g. <span style={{ fontFamily: 'monospace' }}>dpp.ireland.damovohosting.com</span>.
              {' '}A fixed value (e.g. <span style={{ fontFamily: 'monospace' }}>GenesysLAB</span>) forces the same tag on every linked SBC.
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Check size={13}/> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Platform tile ─────────────────────────────────────────────────────────────

function PlatformTile({ p, onConfigure, isAdmin, teamsExtra, onRemove }) {
  const linked = p.primaryVoiceUriId || p.altVoiceUriId || p.url;
  return (
    <div className="platform-tile connected" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
        <div className="platform-logo" style={{ background: p.bg, width: 40, height: 40 }}>
          <span style={{ color: p.color, fontSize: 14, fontWeight: 800 }}>{p.abbr}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div className="platform-name">{p.name}</div>
          <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
            <span className={`badge ${p.type === 'UC' ? 'badge-blue' : 'badge-purple'}`}>{p.type}</span>
            {p.custom && <span className="badge badge-amber">On-Prem</span>}
            {linked
              ? <span className="badge badge-green"><span className="dot green"/>Routing Linked</span>
              : <span className="badge badge-gray"><span className="dot red"/>Not Linked</span>
            }
          </div>
        </div>
        {onRemove && (
          <button
            title="Remove this platform from the customer"
            onClick={() => onRemove(p)}
            style={{ background: 'none', border: 'none', color: 'var(--muted-3)', cursor: 'pointer', padding: 4, flexShrink: 0, display: 'flex' }}
          >
            <X size={15}/>
          </button>
        )}
      </div>

      {/* SIP Trunk URL */}
      {p.url && (
        <div style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 10px' }}>
          <div style={{ fontSize: 10, color: 'var(--muted-3)', marginBottom: 3 }}>SIP Trunk URL</div>
          <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.url}>
            {p.url}
          </div>
        </div>
      )}

      {/* Voice URI link status */}
      <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 10px' }}>
          <div style={{ fontSize: 10, color: 'var(--muted-3)', marginBottom: 3 }}>Primary URI</div>
          {p.primaryUri
            ? <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--ok)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.primaryUri}>
                {p.primaryUriDesc || p.primaryUri}
              </div>
            : <div style={{ fontSize: 11, color: 'var(--muted-3)' }}>Not set</div>
          }
        </div>
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 10px' }}>
          <div style={{ fontSize: 10, color: 'var(--muted-3)', marginBottom: 3 }}>Fallback URI</div>
          {p.altUri
            ? <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--warn)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.altUri}>
                {p.altUriDesc || p.altUri}
              </div>
            : <div style={{ fontSize: 11, color: 'var(--muted-3)' }}>Not set</div>
          }
        </div>
      </div>

      <button className="btn btn-secondary btn-xs" style={{ width: '100%' }} onClick={() => onConfigure(p)}>
        <Settings size={11}/> Configure
      </button>

      {teamsExtra}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PlatformsPage({ user, viewAsCustomer }) {
  const [platforms,  setPlatforms]  = useState([]);
  const [voiceUris,  setVoiceUris]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [configTarget, setConfigTarget] = useState(null);
  const [tab,        setTab]        = useState('all');
  const [flashMsg,   setFlashMsg]   = useState(null);
  const [picking,    setPicking]    = useState(false);

  function flash(type, text) {
    setFlashMsg({ type, text });
    setTimeout(() => setFlashMsg(null), 4000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pData, uData] = await Promise.all([listPlatforms(viewAsCustomer?.id), listVoiceUris()]);
      setPlatforms(pData.platforms || []);
      setVoiceUris(uData.voiceUris || []);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setLoading(false);
    }
  }, [viewAsCustomer]);

  useEffect(() => { load(); }, [load]);

  async function handleSaveConfig(platformId, data) {
    const target = platforms.find(p => p.id === platformId);
    const custId = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.id);
    // Custom (customer-owned) platforms are configured directly on their own row.
    if (target?.custom) {
      await updatePlatform(platformId, data);
    } else if (custId) {
      await updateCustomerPlatform(platformId, data, user?.isAdmin ? custId : undefined);
    } else {
      await updatePlatform(platformId, data);
    }
    setPlatforms(prev => prev.map(p => {
      if (p.id !== platformId) return p;
      const pv = voiceUris.find(u => u.id === data.primaryVoiceUriId);
      const av = voiceUris.find(u => u.id === data.altVoiceUriId);
      return {
        ...p,
        url:               data.url ?? p.url,
        dialPlanName:      data.dialPlanName ?? p.dialPlanName,
        sbcTag:            data.sbcTag ?? p.sbcTag,
        primaryVoiceUriId: data.primaryVoiceUriId,
        primaryUri:        pv?.uri         || null,
        primaryUriDesc:    pv?.description || null,
        altVoiceUriId:     data.altVoiceUriId,
        altUri:            av?.uri         || null,
        altUriDesc:        av?.description || null,
        hasCustomConfig:   Boolean(custId),
      };
    }));
    flash('success', 'Platform configuration saved');
  }

  async function handleEnablePlatform(platformId) {
    try {
      await enableCustomerPlatform(viewAsCustomer.id, platformId);
      setPicking(false);
      await load();
      flash('success', 'Platform added');
    } catch (e) { flash('error', e.message); }
  }
  async function handleDisablePlatform(p) {
    try {
      if (p.custom) {
        if (!window.confirm(`Delete the On-Premise SBC "${p.name}"? This removes it and unsets it from any numbers routed to it.`)) return;
        await deleteCustomPlatform(viewAsCustomer.id, p.id);
        flash('success', `${p.name} deleted`);
      } else {
        await disableCustomerPlatform(viewAsCustomer.id, p.id);
        flash('success', `${p.name} removed`);
      }
      await load();
    } catch (e) { flash('error', e.message); }
  }

  async function handleAddOnPrem() {
    const name = window.prompt('Name for the On-Premise SBC system (e.g. "Acme On-Prem SBC"):');
    if (!name || !name.trim()) return;
    try {
      await createCustomPlatform(viewAsCustomer.id, { name: name.trim(), type: 'UC' });
      await load();
      flash('success', `On-Premise SBC "${name.trim()}" added — now Configure its Voice URI, dial plan and tag`);
    } catch (e) { flash('error', e.message); }
  }

  const isAdmin   = user?.isAdmin;
  const canManage = isAdmin && Boolean(viewAsCustomer);   // admin viewing a customer
  // Shown = platforms enabled for this view (admin-unscoped flags everything on).
  const shown     = platforms.filter(p => p.enabled !== false);
  const addable   = platforms.filter(p => p.enabled === false);  // only populated for canManage
  const filtered  = tab === 'all' ? shown : shown.filter(p => p.type?.toLowerCase() === tab);
  const linked    = shown.filter(p => p.primaryVoiceUriId || p.url).length;

  const teamsCustomerId = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.id);

  return (
    <div className="content">

      {flashMsg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          padding: '10px 16px', borderRadius: 8, fontSize: 13,
          background: flashMsg.type === 'success' ? 'var(--ok-bg)' : 'var(--err-btn-bg)',
          color:      flashMsg.type === 'success' ? 'var(--ok)' : 'var(--err)',
          border:     `1px solid ${flashMsg.type === 'success' ? '#16653444' : 'var(--err-border)'}`,
        }}>{flashMsg.text}</div>
      )}

      {viewAsCustomer && (
        <div className="alert alert-info" style={{ marginBottom: 16, borderColor: '#2563eb44', background: 'var(--info-bg)' }}>
          <Info size={14} style={{ flexShrink: 0, color: 'var(--info)' }}/>
          <span style={{ color: 'var(--info)' }}>
            Viewing platform configuration for <strong style={{ color: 'var(--info)' }}>{viewAsCustomer.name}</strong>.
            Changes made here apply to this customer only.
          </span>
        </div>
      )}
      {!viewAsCustomer && isAdmin && (
        <div className="alert alert-warn" style={{ marginBottom: 16 }}>
          <Info size={14} style={{ flexShrink: 0 }}/>
          <span>Showing global platform defaults. To configure per-customer settings, select a customer from the sidebar.</span>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid mb-20" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        {[
          { label: 'Total Platforms',    value: shown.length,                                      color: 'var(--info)' },
          { label: 'UC Platforms',       value: shown.filter(p => p.type?.toLowerCase() === 'uc').length, color: 'var(--info)' },
          { label: 'CC Platforms',       value: shown.filter(p => p.type?.toLowerCase() === 'cc').length, color: 'var(--purple)' },
          { label: 'Voice URI Linked',   value: linked,                                                color: 'var(--ok)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{loading ? '—' : s.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">UC &amp; CC Platform Connections</div>
            <div className="card-sub">
              Link each platform to a Voxbone Voice URI — customers select a platform name when routing numbers
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', position: 'relative' }}>
            {canManage && (
              <>
                <button className="btn btn-primary btn-sm" onClick={() => setPicking(v => !v)} disabled={loading}>
                  <Plus size={13}/> Add platform
                </button>
                {picking && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
                    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                    boxShadow: '0 10px 30px rgba(0,0,0,.35)', minWidth: 240, maxHeight: 320, overflowY: 'auto', padding: 6,
                  }}>
                    {addable.length === 0 ? (
                      <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--muted-3)' }}>All platforms already added.</div>
                    ) : addable.map(p => (
                      <button
                        key={p.id}
                        onClick={() => handleEnablePlatform(p.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 10px', borderRadius: 6, textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <span className="platform-logo" style={{ background: p.bg, width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: p.color, fontSize: 11, fontWeight: 800 }}>{p.abbr}</span>
                        </span>
                        <span style={{ flex: 1, fontSize: 13, color: 'var(--text-2)' }}>{p.name}</span>
                        <span className={`badge ${p.type === 'UC' ? 'badge-blue' : 'badge-purple'}`}>{p.type}</span>
                      </button>
                    ))}
                  </div>
                )}
                <button className="btn btn-secondary btn-sm" onClick={handleAddOnPrem} disabled={loading} title="Add a customer-specific On-Premise SBC system">
                  <Plus size={13}/> On-Premise SBC
                </button>
              </>
            )}
            {isAdmin && (
              <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
                <RefreshCw size={13} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                Refresh
              </button>
            )}
          </div>
        </div>

        <div style={{ padding: '0 20px' }}>
          <div className="tabs">
            {[['all', 'All Platforms'], ['uc', 'Unified Communications'], ['cc', 'Contact Centre']].map(([id, label]) => (
              <div key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</div>
            ))}
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: 40 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: 40, fontSize: 13, lineHeight: 1.5 }}>
              {canManage
                ? <>No platforms enabled for <strong>{viewAsCustomer.name}</strong> yet. Use <strong>Add platform</strong> to choose the systems they need.</>
                : (viewAsCustomer || !isAdmin)
                  ? 'No platforms have been enabled yet.'
                  : 'No platforms match this filter.'}
            </div>
          ) : (
            <div className="platform-grid">
              {filtered.map(p => (
                <PlatformTile
                  key={p.id}
                  p={p}
                  onConfigure={setConfigTarget}
                  isAdmin={isAdmin}
                  onRemove={canManage ? handleDisablePlatform : undefined}
                  teamsExtra={p.name === 'Microsoft Teams' ? (
                    <TeamsConnector
                      customerId={teamsCustomerId}
                      adminMode={isAdmin}
                      onStatusChange={load}
                    />
                  ) : p.name === 'Zoom Phone' ? (
                    <ZoomConnector
                      customerId={teamsCustomerId}
                      onStatusChange={load}
                    />
                  ) : null}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Help notes */}
      <div className="card mt-16">
        <div className="card-header">
          <div className="card-title">Platform Routing Notes</div>
        </div>
        <div className="card-body">
          {[
            { platform: 'Microsoft Teams',  note: 'Requires Direct Routing in Teams Admin Center. SBC FQDN must match your Microsoft 365 tenant.' },
            { platform: 'Webex Calling',    note: 'Local Gateway model. Register the AudioCodes SBC as a Webex-certified LGW in Control Hub.' },
            { platform: 'Zoom Phone',       note: 'Peer the SBC via Zoom Phone Peering. SBC must be registered with Zoom using TLS on port 5061.' },
            { platform: 'Genesys Cloud',    note: 'Configure BYOC Cloud trunk in Genesys Cloud. AudioCodes SBC acts as the media gateway.' },
            { platform: 'NICE CXone',       note: 'Use CXone SIP Trunk configuration. TLS/SRTP required between SBC and CXone Data Centre.' },
            { platform: 'Five9',            note: 'Add SBC as a VoIP carrier in Five9 Admin. Supports DID mapping and call routing with failover.' },
          ].map(item => (
            <div key={item.platform} className="trunk-row">
              <div style={{ width: 160, fontWeight: 600, fontSize: 13, color: 'var(--text-2)', flexShrink: 0 }}>{item.platform}</div>
              <div style={{ fontSize: 12, color: 'var(--muted-2)' }}>{item.note}</div>
            </div>
          ))}
        </div>
      </div>

      {configTarget && (
        <ConfigureModal
          platform={configTarget}
          voiceUris={voiceUris}
          onClose={() => setConfigTarget(null)}
          onSave={handleSaveConfig}
          user={user}
          viewAsCustomer={viewAsCustomer}
        />
      )}
    </div>
  );
}
